import { socket } from '../core/socket.js';
import { state } from '../core/state.js';
import { t } from '../core/i18n.js';
import { popToast } from '../utils/alerts.js';
import { rtcState } from './rtcState.js';
import { attachSalonStream, detachSalonStream } from './streamUI.js';

// Contexte AudioContext pour la détection de voix (VAD).
// Créé une seule fois au chargement du module.
export const audioContext = new (window.AudioContext || window.webkitAudioContext)();

// ==========================================================================
//   CONNEXIONS P2P
// ==========================================================================

export function creerConnexionPair(pseudoDistant) {
    const peerConnection = new RTCPeerConnection(rtcState.configWebRTC);

    if (rtcState.fluxAudioLocal) {
        rtcState.fluxAudioLocal.getTracks().forEach(track => {
            peerConnection.addTrack(track, rtcState.fluxAudioLocal);
        });
    }

    // Si un stream est déjà actif, l'injecter dans cette nouvelle connexion
    // (couvre le cas où un pair rejoint le vocal après le début du stream).
    if (rtcState.fluxVideoLocal) {
        rtcState.fluxVideoLocal.getTracks().forEach(track => {
            const sender = peerConnection.addTrack(track, rtcState.fluxVideoLocal);
            rtcState.screenShareSenders.push({ pc: peerConnection, sender });
        });
    }

    peerConnection.onicecandidate = (event) => {
        if (event.candidate) {
            socket.emit('webrtc-ice-candidate', {
                targetId: pseudoDistant,
                candidate: event.candidate
            });
        }
    };

    peerConnection.ontrack = (event) => {
        const streamDistant = event.streams[0];
        const typePiste = event.track.kind;

        if (typePiste === 'audio') {
            // Si le stream contient aussi une piste vidéo, c'est l'audio du screen share :
            // il joue déjà via l'élément <video> → on démute juste la vidéo.
            if (streamDistant.getVideoTracks().length > 0) {
                const videoEl = document.getElementById(`video-${pseudoDistant}`);
                if (videoEl) videoEl.muted = false;
                return;
            }

            let audioEl = rtcState.elementsAudio[pseudoDistant];
            if (!audioEl) {
                audioEl = document.createElement('audio');
                audioEl.autoplay = true;
                audioEl.playsInline = true;
                document.body.appendChild(audioEl);
                rtcState.elementsAudio[pseudoDistant] = audioEl;
            }
            audioEl.srcObject = streamDistant;
            const _vol = (rtcState.volumesUtilisateurs[pseudoDistant] ?? 100) / 100;
            audioEl.volume = Math.min(1, Math.max(0, _vol));
            audioEl.play().catch(() => {});

            detecterVoix(streamDistant, (parle) => {
                if (parle) rtcState.speakingNow.add(pseudoDistant);
                else       rtcState.speakingNow.delete(pseudoDistant);
                const miniAvatarDistant = document.getElementById(`mini-avatar-${pseudoDistant}`);
                if (miniAvatarDistant) miniAvatarDistant.classList.toggle('is-speaking-border', parle);
                const mpcRemote = document.getElementById('mpc-avatar-remote');
                if (mpcRemote && mpcRemote.dataset.pseudo === pseudoDistant) {
                    mpcRemote.classList.toggle('is-speaking-border', parle);
                }
            });
        } else if (typePiste === 'video') {
            const banner    = document.getElementById('mp-call-banner');
            const streamZone = document.getElementById('mpc-stream-zone');
            const estEnMP   = banner && banner.style.display !== 'none' && banner.dataset.cible === pseudoDistant;

            let videoEl = document.getElementById(`video-${pseudoDistant}`);
            if (!videoEl) {
                videoEl = document.createElement('video');
                videoEl.id = `video-${pseudoDistant}`;
                videoEl.autoplay = true;
                videoEl.playsInline = true;
                videoEl.muted = true;
                videoEl.className = 'stream-video-element';
                videoEl.style.display = 'none'; // affiché uniquement sur action de l'utilisateur

                if (estEnMP && streamZone) {
                    videoEl.ondblclick = () => {
                        if (document.pictureInPictureElement) document.exitPictureInPicture().catch(() => {});
                        else videoEl.requestPictureInPicture?.().catch(() => {});
                    };
                    const pipMp = document.getElementById('btn-pip-mp');
                    if (pipMp) {
                        pipMp.style.display = 'inline-flex';
                        pipMp.onclick = () => {
                            if (document.pictureInPictureElement) document.exitPictureInPicture().catch(() => {});
                            else videoEl.requestPictureInPicture?.().catch(() => {});
                        };
                    }
                }
                document.body.appendChild(videoEl);
            }

            videoEl.srcObject = streamDistant;
            videoEl.play().catch(() => {});

            // En MP : afficher directement dans la zone d'appel (c'est intentionnel)
            if (estEnMP && streamZone) {
                if (!streamZone.contains(videoEl)) streamZone.appendChild(videoEl);
                streamZone.classList.add('has-stream');
                videoEl.style.display = 'block';
            } else {
                // En salon : indicateur discret (🖥️ + glow), l'utilisateur choisit de regarder
                rtcState.pseudosEnStream.add(pseudoDistant);
                _majIndicateurStream(pseudoDistant, true);
            }

            event.track.addEventListener('ended', () => {
                videoEl.pause();
                videoEl.srcObject = null;
                videoEl.remove();
                detachSalonStream(pseudoDistant);
                rtcState.pseudosEnStream.delete(pseudoDistant);
                _majIndicateurStream(pseudoDistant, false);
                rtcState.speakingNow.delete(pseudoDistant);
            });
        }
    };

    rtcState.connexionsPairs[pseudoDistant] = peerConnection;
    return peerConnection;
}

export async function appelerAgent(pseudoDistant) {
    const pc = creerConnexionPair(pseudoDistant);

    // Spectateur sans flux audio : l'offre serait vide sans transceivers recvonly.
    if (!rtcState.fluxAudioLocal && !rtcState.fluxVideoLocal) {
        pc.addTransceiver('audio', { direction: 'recvonly' });
        pc.addTransceiver('video', { direction: 'recvonly' });
    }

    try {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        socket.emit('webrtc-offer', { targetId: pseudoDistant, sdp: pc.localDescription });
    } catch (err) {
        console.error(`❌ Erreur lors de l'appel vers ${pseudoDistant}:`, err);
    }
}

// Ouvrir le stream d'un pair (action volontaire de l'utilisateur).
export function ouvrirStreamChoisi(pseudo) {
    const videoEl = document.getElementById(`video-${pseudo}`);
    if (!videoEl) return;
    attachSalonStream(pseudo, videoEl);
    videoEl.style.display = 'block';
    socket.emit('stream-je-regarde', { salonChatId: state.salonActuelID });
}

// Mettre à jour l'indicateur 🖥️ sur le tag vocal d'un pair.
function _majIndicateurStream(pseudo, actif) {
    const tag = document.querySelector(`.vocal-agent-tag[data-pseudo="${CSS.escape(pseudo)}"]`);
    if (!tag) return;
    const existing = tag.querySelector('.stream-badge-icone');
    const pseudoSpan = tag.querySelector('.vocal-pseudo');
    if (actif && !existing) {
        const icone = document.createElement('span');
        icone.className = 'stream-badge-icone';
        icone.title = 'Partage d\'écran actif — cliquez pour regarder';
        icone.innerHTML = '<i class="fas fa-desktop"></i>';
        icone.onclick = (e) => { e.stopPropagation(); ouvrirStreamChoisi(pseudo); };
        tag.classList.add('is-streaming');
        tag.appendChild(icone);
        if (pseudoSpan) {
            const couleur = (state.utilisateurs[pseudo] || {}).couleur || '#ffb75e';
            pseudoSpan.style.background = couleur + '28';
            pseudoSpan.style.boxShadow  = `0 0 8px ${couleur}99`;
            pseudoSpan.style.borderRadius = '4px';
            pseudoSpan.style.padding = '1px 5px';
        }
    } else if (!actif && existing) {
        existing.remove();
        tag.classList.remove('is-streaming');
        if (pseudoSpan) pseudoSpan.style.cssText = '';
    }
}

// ==========================================================================
//   SIGNALING SOCKET
// ==========================================================================

socket.on('webrtc-offer', async (data) => {
    const { sdp, callerId } = data;
    let pc = rtcState.connexionsPairs[callerId];
    if (!pc) pc = creerConnexionPair(callerId);
    await pc.setRemoteDescription(sdp);
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    socket.emit('webrtc-answer', { targetId: callerId, sdp: pc.localDescription });
});

socket.on('webrtc-answer', async (data) => {
    const { sdp, answererId } = data;
    const pc = rtcState.connexionsPairs[answererId];
    if (pc) await pc.setRemoteDescription(sdp);
});

socket.on('webrtc-ice-candidate', async (data) => {
    const { candidate, senderId } = data;
    const pc = rtcState.connexionsPairs[senderId];
    if (pc && candidate) await pc.addIceCandidate(new RTCIceCandidate(candidate));
});

// ==========================================================================
//   DÉTECTION DE VOIX (VAD)
// ==========================================================================

export function detecterVoix(stream, callback) {
    if (audioContext.state === 'suspended') audioContext.resume();

    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 512;
    analyser.smoothingTimeConstant = 0.4;
    source.connect(analyser);

    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    function verifierVolume() {
        if (!localStorage.getItem('memoireVocalId')) {
            source.disconnect();
            return;
        }
        analyser.getByteFrequencyData(dataArray);
        let somme = 0;
        for (let i = 0; i < dataArray.length; i++) somme += dataArray[i];
        callback(somme / dataArray.length > 15);
        requestAnimationFrame(verifierVolume);
    }
    verifierVolume();
}

// ==========================================================================
//   BADGES LIVE & STREAM
// ==========================================================================

export function ajouterBadgeLive(pseudo) {
    if (document.getElementById(`live-badge-${pseudo}`)) return;

    const avatar = document.getElementById(`mini-avatar-${pseudo}`);
    if (avatar && avatar.parentElement) {
        const badge = document.createElement('span');
        badge.id = `live-badge-${pseudo}`;
        badge.className = 'live-badge';
        badge.innerHTML = t('vocal.live');
        badge.title = t('vocal.stream_click');
        badge.onclick = (e) => {
            e.stopPropagation();
            ouvrirStreamChoisi(pseudo);
        };
        avatar.parentElement.appendChild(badge);
    }

    const watchBtn = document.getElementById('mp-watch-stream-btn');
    const mpBanner = document.getElementById('mp-call-banner');
    if (watchBtn && mpBanner && mpBanner.style.display !== 'none' && mpBanner.dataset.cible === pseudo) {
        watchBtn.style.display = 'inline-block';
    }
}

export function regarderStreamMP() {
    const mpBanner = document.getElementById('mp-call-banner');
    if (mpBanner && mpBanner.dataset.cible) {
        basculerAffichageStream(mpBanner.dataset.cible);
    }
}

export function basculerAffichageStream(pseudo) {
    const videoEl = document.getElementById(`video-${pseudo}`);
    if (videoEl) {
        if (videoEl.style.display === 'none') {
            videoEl.style.display = 'block';
            popToast(t('toast.watching_stream', { pseudo }), 'stream');
        } else {
            videoEl.style.display = 'none';
            popToast(t('toast.stream_hidden', { pseudo }));
        }
    }
}

// ==========================================================================
//   SCREEN SHARE
// ==========================================================================

export async function toggleScreenShare() {
    if (rtcState.fluxVideoLocal) {
        arreterScreenShare();
    } else {
        await startScreenShare();
    }
}

export async function startScreenShare() {
    try {
        rtcState.fluxVideoLocal = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
        const pisteVideo = rtcState.fluxVideoLocal.getVideoTracks()[0];
        rtcState.screenShareSenders = [];
        popToast(t('toast.screen_share_on'), 'stream');
        const btnStream = document.getElementById('btn-stream');
        if (btnStream) btnStream.style.color = '#f85149';

        let localVideo = document.getElementById('video-local-preview');
        if (!localVideo) {
            localVideo = document.createElement('video');
            localVideo.id = 'video-local-preview';
            localVideo.autoplay = true;
            localVideo.muted = true;
            localVideo.className = 'stream-video-element';

            const banner     = document.getElementById('mp-call-banner');
            const streamZone = document.getElementById('mpc-stream-zone');
            const estEnMP    = banner && banner.style.display !== 'none' && banner.dataset.cible;

            if (estEnMP && streamZone) {
                streamZone.appendChild(localVideo);
                streamZone.classList.add('has-stream');
                const pipMp = document.getElementById('btn-pip-mp');
                if (pipMp) {
                    pipMp.style.display = 'inline-flex';
                    pipMp.onclick = () => {
                        if (document.pictureInPictureElement) document.exitPictureInPicture().catch(() => {});
                        else localVideo.requestPictureInPicture?.().catch(() => {});
                    };
                }
            } else {
                attachSalonStream(state.monPseudo, localVideo);
            }
        }
        localVideo.srcObject = rtcState.fluxVideoLocal;

        for (const pseudo of Object.keys(rtcState.connexionsPairs)) {
            const pc = rtcState.connexionsPairs[pseudo];
            // Relay toutes les pistes (vidéo + audio éventuel du screen share)
            for (const track of rtcState.fluxVideoLocal.getTracks()) {
                const sender = pc.addTrack(track, rtcState.fluxVideoLocal);
                rtcState.screenShareSenders.push({ pc, sender });
            }
            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);
            socket.emit('webrtc-offer', { targetId: pseudo, sdp: pc.localDescription });
        }

        pisteVideo.onended = arreterScreenShare;

        // Notifier le salon de chat qu'un stream est actif
        // (permet aux spectateurs non-vocaux de recevoir le stream)
        if (state.salonActuelID) {
            socket.emit('stream-demare', { salonChatId: state.salonActuelID });
        }
    } catch (err) {
        console.error("❌ Erreur partage d'écran :", err);
        popToast(t('toast.screen_share_error'), 'error');
    }
}

export function arreterScreenShare() {
    if (!rtcState.fluxVideoLocal) return;

    rtcState.fluxVideoLocal.getTracks().forEach(track => track.stop());
    rtcState.fluxVideoLocal = null;
    popToast(t('toast.screen_share_off'));

    const btnStream = document.getElementById('btn-stream');
    if (btnStream) btnStream.style.color = '#8b949e';

    const localVideo = document.getElementById('video-local-preview');
    if (localVideo) {
        localVideo.pause();
        localVideo.srcObject = null;   // libère le buffer → plus de frame gelé
        localVideo.remove();
    }

    // Nettoyer le stream viewer salon (l'émetteur ne reçoit pas webrtc-remove-stream)
    detachSalonStream(state.monPseudo);

    // Nettoyer la zone MP si on était en appel
    const streamZone = document.getElementById('mpc-stream-zone');
    if (streamZone) streamZone.classList.remove('has-stream');
    const pipMp = document.getElementById('btn-pip-mp');
    if (pipMp) { pipMp.style.display = 'none'; pipMp.onclick = null; }
    if (document.pictureInPictureElement) document.exitPictureInPicture().catch(() => {});

    // Retirer tous les senders screen share (vidéo + audio)
    rtcState.screenShareSenders.forEach(({ pc, sender }) => {
        try { pc.removeTrack(sender); } catch (_) {}
    });
    rtcState.screenShareSenders = [];

    const salonId     = localStorage.getItem('memoireVocalId');
    const salonChatId = state.salonActuelID;
    const mpBanner    = document.getElementById('mp-call-banner');
    const targetPseudo = (!salonId && mpBanner?.dataset.cible) ? mpBanner.dataset.cible : null;
    socket.emit('webrtc-stop-stream', { salonId, salonChatId, pseudo: state.monPseudo, targetPseudo });
    if (salonChatId) socket.emit('stream-arrete', { salonChatId });
}

socket.on('webrtc-remove-stream', (pseudoDistant) => {
    rtcState.pseudosEnStream.delete(pseudoDistant);
    _majIndicateurStream(pseudoDistant, false);

    const videoEl = document.getElementById(`video-${pseudoDistant}`);
    if (videoEl) {
        videoEl.pause();
        videoEl.srcObject = null;
        videoEl.remove();
    }

    const badge = document.getElementById(`live-badge-${pseudoDistant}`);
    if (badge) badge.remove();

    const watchBtn = document.getElementById('mp-watch-stream-btn');
    if (watchBtn) watchBtn.style.display = 'none';

    const pipMp = document.getElementById('btn-pip-mp');
    if (pipMp) { pipMp.style.display = 'none'; pipMp.onclick = null; }

    const streamZone = document.getElementById('mpc-stream-zone');
    if (streamZone) streamZone.classList.remove('has-stream');

    // Nettoyer le viewer salon
    detachSalonStream(pseudoDistant);
    rtcState.speakingNow.delete(pseudoDistant);

    if (document.pictureInPictureElement) document.exitPictureInPicture().catch(() => {});
});

// Un stream est actif dans le salon actuel (émetteur pas forcément dans le vocal).
// Mémoriser qui streame et, si pas de connexion existante, en créer une.
socket.on('stream-actif-salon', ({ pseudo: pseudoStreamer }) => {
    if (!pseudoStreamer || pseudoStreamer === state.monPseudo) return;
    rtcState.pseudosEnStream.add(pseudoStreamer);
    _majIndicateurStream(pseudoStreamer, true);
    if (rtcState.connexionsPairs[pseudoStreamer]) return; // connexion déjà établie
    appelerAgent(pseudoStreamer);
});
